package com.trevorwiebe.timesheet.loads.presentation.driver

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverLoadsEvent
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverLoadsState

/** Loads screen shown to DRIVER users: their dispatch-confirmed loads grouped by date. */
@OptIn(ExperimentalFoundationApi::class)
@Composable
fun DriverLoadsScreen(
    state: DriverLoadsState,
    documentPdfs: Map<String, ByteArray>,
    onEvent: (DriverLoadsEvent) -> Unit,
) {
    Scaffold(
        topBar = { TopBar("Loads") }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
            when {
                state.isLoading -> Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                }

                state.error != null -> Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    TextButton(
                        onClick = { onEvent(DriverLoadsEvent.OnRetry) }
                    ) {
                        Text("Couldn't load your loads. Tap to retry.")
                    }
                }

                state.groupedLoads.isEmpty() -> Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        modifier = Modifier.padding(16.dp),
                        text = "No loads assigned to you yet.\n" +
                            "Loads appear here once dispatch assigns and confirms them.",
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center,
                    )
                }

                else -> LazyColumn(modifier = Modifier.fillMaxSize()) {
                    state.groupedLoads.forEach { group ->
                        stickyHeader(key = "header-${group.label}") {
                            Text(
                                text = group.label,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(MaterialTheme.colorScheme.background)
                                    .padding(horizontal = 16.dp, vertical = 8.dp),
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.tertiary,
                            )
                        }
                        items(
                            count = group.loads.size,
                            key = { index -> group.loads[index].id },
                        ) { index ->
                            val load = group.loads[index]
                            val isDocumentLoad = state.documentLoadId == load.id
                            DriverLoadCard(
                                load = load,
                                documentPdfs = documentPdfs,
                                isSavingDocument = isDocumentLoad && state.isSavingDocument,
                                documentError = state.documentError.takeIf { isDocumentLoad },
                                onEvent = onEvent,
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 6.dp),
                            )
                        }
                    }
                }
            }
        }
    }
}
